import { AD_CLICK_URL_ONLY_KEYS, buildPathFromSafeQuery, readStoredAttribution } from './attribution';

// ABSITE intent the quiz builder can act on without router state: the
// visitor's acquisition campaign, and an explicit ?practice=absite link.
// Both only ever choose what the builder SHOWS (the one-tap ABSITE practice
// card); a quiz is still created only when the student presses Start.

// Campaigns that name ABSITE: qlearn_absite_search_s2 (paid search),
// absite_topic_*, absite_landing_*, absite_guide_* (www ABSITE pages),
// absite_practice_* (practice emails). Matched case-insensitively anywhere in
// the value.
export const isAbsiteCampaign = (campaign) =>
  typeof campaign === 'string' && /absite/i.test(campaign);

// The FIRST touch only: the campaign that brought the visitor to QLearn, not a
// later click. Stored attribution expires after 90 days (attribution.js). The
// native app stores utm_campaign like the web does (only `ref` is dropped
// there), so this reads the same on both.
export const absiteCampaignFirstTouch = (storedAttribution = readStoredAttribution()) =>
  isAbsiteCampaign(storedAttribution?.firstTouch?.params?.utm_campaign);

// /create-quiz?practice=absite: "start ABSITE practice", carried in the
// address so it survives a sign-in (the day-2 practice email links here). The
// name is exact; the value is matched case-insensitively and ABSITE is the
// only one accepted. Anything else is ignored.
export const PRACTICE_QUERY_KEY = 'practice';
const PRACTICE_QUERY_ABSITE = 'absite';
export const PRACTICE_ABSITE_PATH = `/create-quiz?${PRACTICE_QUERY_KEY}=${PRACTICE_QUERY_ABSITE}`;

export const resolvePracticeQuery = (search) => {
  let value;
  try {
    value = new URLSearchParams(search || '').get(PRACTICE_QUERY_KEY);
  } catch (_) {
    return '';
  }
  return typeof value === 'string' && value.toLowerCase() === PRACTICE_QUERY_ABSITE ? 'ABSITE' : '';
};

// React Router matches /create-quiz case-insensitively and with a trailing slash.
const isCreateQuizPath = (pathname) =>
  typeof pathname === 'string' && pathname.toLowerCase().replace(/\/+$/, '') === '/create-quiz';

// Whether a same-site path is the ABSITE practice entry: /create-quiz with
// ?practice=absite, the value in any case and in any position among other
// keys. /login reads its ?next= with this (after its own allowlist) to greet
// that visitor, who mostly arrives from www's ABSITE pages or a practice email
// and has left nothing to continue, with the ABSITE line.
const SAME_SITE_BASE = 'https://qlearn.local';

export const isPracticeEntryPath = (path) => {
  if (typeof path !== 'string' || !path.startsWith('/')) {
    return false;
  }
  let parsed;
  try {
    parsed = new URL(path, SAME_SITE_BASE);
  } catch (_) {
    return false;
  }
  return parsed.origin === SAME_SITE_BASE
    && isCreateQuizPath(parsed.pathname)
    && resolvePracticeQuery(parsed.search) === 'ABSITE';
};

// Where a signed-out visitor on /create-quiz?practice=absite signs in: /login
// with ?next= returning to the practice entry (rebuilt here, never copied, so
// nothing else in the address can ride along) and the campaign keys /login
// itself keeps (the same allowlist as the / and /register redirects). `ref`
// never rides: store.js captured it from this address already. undefined for
// every other address (createQuizLoginPath below covers the rest of Create
// Quiz).
export const practiceEntryLoginPath = (pathname, search) => {
  if (!isCreateQuizPath(pathname) || !resolvePracticeQuery(search)) {
    return undefined;
  }
  const params = new URLSearchParams(search || '');
  params.delete('ref');
  params.delete(PRACTICE_QUERY_KEY);
  params.set('next', PRACTICE_ABSITE_PATH);
  return buildPathFromSafeQuery('/login', params.toString(), {
    safeExtraParamKeys: ['next'],
    urlOnlyParamKeys: AD_CLICK_URL_ONLY_KEYS,
  });
};

// Create Quiz's own path, as /login's ?next= allowlist spells it.
export const CREATE_QUIZ_PATH = '/create-quiz';

// The same sign-in for every other /create-quiz address: the general practice
// email and ads link to plain /create-quiz?utm_…, and a signed-out visitor
// there used to be sent to a bare "/" (campaign keys and ad click ids gone
// from the address before GTM's tags could read them). /login gets ?next=
// back to Create Quiz, the pathname only (nothing else in the address can ride
// in it), and the same campaign keys and ad click ids as the practice entry.
// undefined for the practice entry itself (practiceEntryLoginPath) and for
// every other page.
export const createQuizLoginPath = (pathname, search) => {
  if (!isCreateQuizPath(pathname) || resolvePracticeQuery(search)) {
    return undefined;
  }
  const params = new URLSearchParams(search || '');
  params.delete('ref');
  params.set('next', CREATE_QUIZ_PATH);
  return buildPathFromSafeQuery('/login', params.toString(), {
    safeExtraParamKeys: ['next'],
    urlOnlyParamKeys: AD_CLICK_URL_ONLY_KEYS,
  });
};
